import * as React from 'react';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from '../../../components-v2/Combobox';
import { INameValue } from '../ValueSelector';
import CountriesCollection from './countries.json';
import { styleSheet } from './styles';

export const Countries = CountriesCollection as INameValue[];

// The collection carries a dashed row dividing the three common countries from the rest, which a
// searchable list has no use for. Kept out of `Countries` itself since that export feeds lookups.
const SELECTABLE_COUNTRIES = Countries.filter(country => country.value !== 'None');

// The Combobox wrapper widens base-ui's item generic to `unknown`, so items narrow at the boundary.
const countryOf = (value: unknown) => value as INameValue | null;

interface IProps {
	className?: string;
	onSelectionChanged?(selected: INameValue): void;
	selectedValue?: string;
}

export const CountrySelector: React.FC<IProps> = ({ className, onSelectionChanged, selectedValue }) => {
	// Addresses store the country code, but older records hold a free-text country name. Echoing the
	// stored string back as its own option keeps those readable rather than showing an empty input.
	const selectedCountry =
		SELECTABLE_COUNTRIES.find(country => country.value === selectedValue) ??
		(selectedValue ? { name: selectedValue, value: selectedValue } : null);

	return (
		<div className={`country-selector ${className || ''}`}>
			<Combobox
				isItemEqualToValue={(item, value) => countryOf(item)?.value === countryOf(value)?.value}
				itemToStringLabel={country => countryOf(country)?.name ?? ''}
				items={SELECTABLE_COUNTRIES}
				onValueChange={value => {
					const country = countryOf(value);
					if (country) {
						onSelectionChanged?.(country);
					}
				}}
				value={selectedCountry}
			>
				<ComboboxInput placeholder='Search countries' triggerAriaLabel='Show countries' />
				<ComboboxContent styles={[styleSheet.content]}>
					<ComboboxEmpty>No countries found</ComboboxEmpty>
					<ComboboxList>
						{(country: INameValue) => (
							<ComboboxItem key={country.value} value={country}>
								{country.name}
							</ComboboxItem>
						)}
					</ComboboxList>
				</ComboboxContent>
			</Combobox>
		</div>
	);
};
